import type { FC } from 'react';
import { useCallback, useContext, useState } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { default as OnScrollBottom, DISTANCE_UNIT } from '@wix/da-shared-react/pkg/OnScrollBottom';
import { useStream } from '@wix/da-shared-react/pkg/utils/hooks/useStream';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import ArrowDown from '@wix/da-ds/pkg/Icons/16x16/ArrowDown';
import { sanitizeHref } from '@wix/da-url';
import generatePaginationUrl from '../../../../utils/generatePaginationUrl';

import s from './InfiniteScrollControls.scss';

const DISTANCE_FROM_BOTTOM_TO_FETCH_MORE = 1000;

export interface Props {
  streamId: string;
  currentPage: number;
  requestUrl: string;
  isDuperbrowseActive: boolean;
  disableInfiniteScroll?: boolean;
  error?: string;
}

const InfiniteScrollControls: FC<Props> = ({
  streamId,
  currentPage,
  requestUrl,
  isDuperbrowseActive,
  disableInfiniteScroll = false,
  error,
}) => {
  const { t } = useTranslation();
  const { isFetching, hasMore, fetchNext } = useStream(streamId);
  const isMobile = useContext(MobileContext);
  const { isLoggedIn } = useContext(SessionContext);
  const [mobileInfScrollStarted, setMobileInfScrollStarted] = useState(false);

  const handleScrollToBottom = useCallback(() => {
    if (isFetching || isDuperbrowseActive || !hasMore || error || disableInfiniteScroll) {
      return;
    }

    fetchNext();
  }, [disableInfiniteScroll, error, fetchNext, hasMore, isDuperbrowseActive, isFetching]);

  const shouldRequireInfiniteScrollStart = !disableInfiniteScroll && !isLoggedIn && isMobile;

  return (
    <>
      {isFetching && <LoadingIndicator />}
      {hasMore && shouldRequireInfiniteScrollStart && (
        <div className={cn(s['start-button'], mobileInfScrollStarted && s['hidden'])}>
          <SolidButton
            icon={ArrowDown}
            postfixIcon={ArrowDown}
            variant={'approval-green'}
            size={'large'}
            href={sanitizeHref(generatePaginationUrl(currentPage + 1, requestUrl))}
            onClick={e => {
              e.preventDefault();
              setMobileInfScrollStarted(true);
            }}
          >
            {t('common.startInfiniteScroll')}
          </SolidButton>
        </div>
      )}
      {(mobileInfScrollStarted ||
        (!disableInfiniteScroll && !shouldRequireInfiniteScrollStart)) && (
        <OnScrollBottom
          checkOnMount
          checkOnUpdate={hasMore}
          onScrolledToBottom={handleScrollToBottom}
          distanceToBottom={DISTANCE_FROM_BOTTOM_TO_FETCH_MORE}
          distanceUnit={DISTANCE_UNIT.PX}
        />
      )}
    </>
  );
};

InfiniteScrollControls.displayName = 'InfiniteScrollControls';

export default InfiniteScrollControls;
